import { useRouter } from 'expo-router';
import BenefitItem from 'features/v2a/receiver/components/BenefitItem/BenefitItem';
import PaymentTransferring from 'features/v2a/receiver/components/PaymentTransferring/PaymentTransferring';
import {
  buildSignUpUrlParams,
  decodeJWT,
  getCdnURL,
} from 'features/v2a/receiver/helpers/V2A.helpers';
import { V2AReceiverContext } from 'features/v2a/receiver/providers/V2AReceiverContextProvider';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, StyleSheet, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import {
  EnvironmentContext,
  EnvironmentContextProps,
} from 'shared/providers/environment';
import theme from 'shared/styles/theme';

export interface Benefit {
  icon: string;
  title: string;
}
const DEFAULT_ANIMATION_TIME = 5000;

const PaymentsConfirmation = () => {
  const router = useRouter();
  const [loading, setLoading] = useState(true);

  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'paymentConfirmation',
  });
  const { paymentDetails } = useContext(V2AReceiverContext);
  const benefits: Benefit[] = [
    {
      title: translatedText('benefit1'),
      icon: 'NO_FEES',
    },
    {
      title: translatedText('benefit2'),
      icon: 'EARLY_PAYDAY',
    },
    {
      title: translatedText('benefit3'),
      icon: 'DEPOSIT_CASH',
    },
  ];

  const decoded = paymentDetails && decodeJWT(paymentDetails);
  const externalId = decoded?.rid as string;
  const customAttributes = { receiver_id: externalId };
  const { currentEnv } =
    useContext<EnvironmentContextProps>(EnvironmentContext);
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, DEFAULT_ANIMATION_TIME);

    return () => clearTimeout(timer);
  }, []);

  useViewDidFocusScreenAnalyticsEvent({
    key: '/payments-confirmation/v2aReceiverPaymentSuccess',
    customAttributes: customAttributes,
  });

  const logTransferSuccessAnalytics = usePressableAnalytics({
    analyticsId: 'v2aReceiverTransferSuccessful',
    customAttributes: customAttributes,
  });

  useEffect(() => {
    if (!loading) {
      logTransferSuccessAnalytics();
    }
  }, [loading, logTransferSuccessAnalytics]);

  const openAgreementPdf = async () => {
    const agreementPdfUrl = `${getCdnURL(currentEnv)}/agreements/f234999f-7c35-4bf4-9e50-28817e26def4.pdf`;
    const canOpen = await Linking.canOpenURL(agreementPdfUrl);
    if (canOpen) {
      await Linking.openURL(agreementPdfUrl);
    }
  };
  return (
    <View style={styles.container}>
      <SuccessScreen
        visual="PLANE_A_ANIMATED"
        titleText={translatedText('title')}
        primaryButton={{
          label: translatedText('button'),
          onPress: () => {
            if (paymentDetails) {
              const signupUrlParams = buildSignUpUrlParams(paymentDetails);
              router.dismissTo({
                pathname: '/signup',
                params: signupUrlParams,
              });
            }
          },
          analyticsId: 'v2aReceiverTransferSignupClicked',
          customAttributes: customAttributes,
        }}
        content={
          <View>
            <View style={styles.contentContainer}>
              <BodyBaseText numberOfLines={1} style={styles.headline}>
                {translatedText('headline', {
                  amount: paymentDetails?.amount,
                })}
              </BodyBaseText>
              <HairLineDivider />
              <TitleLgText numberOfLines={1} style={styles.subtitle}>
                <TitleLgText numberOfLines={1}>
                  {translatedText('subtitle1')}
                </TitleLgText>
                <TitleLgText numberOfLines={1} style={styles.subtitleStrong}>
                  {translatedText('subtitle2')}
                </TitleLgText>
              </TitleLgText>
              <BodySmText numberOfLines={0} style={styles.description}>
                {translatedText('description')}
              </BodySmText>
            </View>
            <FlatList
              data={benefits}
              renderItem={({ item, index }) => (
                <BenefitItem
                  item={item}
                  showHairline={index < benefits.length - 1}
                />
              )}
              keyExtractor={(item) => item.icon}
              contentContainerStyle={styles.listItem}
              removeClippedSubviews={false}
            />
            <BodyXSText numberOfLines={1} style={styles.disclaimer}>
              <BodyXSText numberOfLines={1}>
                {translatedText('disclaimerText')}
              </BodyXSText>
              <HyperlinkSmText
                numberOfLines={1}
                style={styles.disclaimerLink}
                onPress={openAgreementPdf}
              >
                {translatedText('disclaimerLink')}
              </HyperlinkSmText>
            </BodyXSText>
          </View>
        }
      />
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<PaymentTransferring />}
        ErrorComponent={undefined}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: theme.spacing.m,
  },
  description: {
    paddingTop: theme.spacing.m,
  },
  disclaimer: {
    paddingBottom: theme.spacing.xl,
    paddingTop: theme.spacing.s,
  },
  disclaimerLink: {
    paddingLeft: theme.spacing.xs,
  },
  headline: {
    paddingBottom: theme.spacing.m,
    textAlign: 'center',
  },
  listItem: {
    marginLeft: -theme.spacing.s,
  },
  subtitle: {
    paddingBottom: theme.spacing.s,
    paddingTop: theme.spacing.m,
  },
  subtitleStrong: {
    color: theme.color.interactive.primary,
    marginLeft: theme.spacing.xs,
  },
});

export default PaymentsConfirmation;
